import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import i18n from 'i18n-js';
import * as Yup from 'yup';
import { Formik, Field } from 'formik';
import Grid from '@material-ui/core/Grid';
import useStyles from './Accounting.styled';
import { SkusActions } from 'state/modules/skus';
import { Box, Divider, Tooltip } from '@material-ui/core';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import { TaxTypesSelect } from 'components/DataFields';
import { TaxRulesetsSelect, AccountingTypesSelect } from 'components/AsyncFields';
import { TextField, RadioButton, RadioButtonsGroup } from 'components/FormFields';
import InfoIcon from '@material-ui/icons/Info';
import { generateTaxTypes } from 'constants';
import { ability } from '../../../Can';

const SkuAccounting = ({
  id,
  formRef,
  errors: propErrors,
  step = {},
  exit,
  onSave,
  center = true,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const pricingConfig = useSelector((state) => state.config.settings?.core?.pricing || {});
  const taxesEnabled = useSelector((state) => state.config.settings?.core?.receivable?.taxes);
  const holdEnabled = useSelector(
    (state) => state.config.settings?.core?.payment?.authorization_hold_enabled || false
  );

  const initialValues = {
    accounting_type: step?.accounting_type,
    taxing_choice: step?.taxing_choice || (taxesEnabled === false ? 'EXEMPT' : ''),
    accounting_type_id: step?.accounting_type_id || '',
    tax_rate_default: step?.tax_rate_default,
    tax_rules_set_id: step?.tax_rules_set?.id || '',
  };

  const TRSEnabled = useSelector(
    (state) => state.config.settings?.core?.pricing?.tax_rules_set?.enabled || false
  );
  const allowTaxPerLocation = TRSEnabled && ability.can('read', 'tax_rules_set');
  const taxTypes = generateTaxTypes(allowTaxPerLocation ? [] : ['PERLOCATION']).map(
    (tax) => tax.value
  );

  const formSchema = Yup.object().shape({
    accounting_type: Yup.string().required(i18n.t('common.required_field')),
    accounting_type_id: Yup.string().required(i18n.t('common.required_field')),
    taxing_choice: Yup.string().when('accounting_type', {
      is: 'REVENUE',
      then: Yup.string()
        .required(i18n.t('common.required_field'))
        .oneOf(taxTypes, i18n.t('common.valid_tax_type')),
      otherwise: Yup.string().notRequired(),
    }),
    tax_rate_default: Yup.number().when(['accounting_type', 'taxing_choice'], {
      is: (accounting_type, taxing_choice) =>
        accounting_type === 'REVENUE' && ['DEFAULT', 'PERLOCATION'].includes(taxing_choice),
      then: Yup.number()
        .min(0, i18n.t('common.min_0'))
        .max(1, i18n.t('common.max_1'))
        .required(i18n.t('common.required_field')),
      otherwise: Yup.number().nullable().notRequired(), // Allow null for non-REVENUE
    }),
    tax_rules_set_id: Yup.number().when(['accounting_type', 'taxing_choice'], {
      is: (accounting_type, taxing_choice) =>
        accounting_type === 'REVENUE' && taxing_choice === 'PERLOCATION',
      then: Yup.number().required(i18n.t('common.required_field')),
      otherwise: Yup.number().nullable().notRequired(), // Allow null for non-REVENUE
    }),
  });

  const onSubmit = (values) => {
    const submitValues = { ...values };

    if (submitValues.accounting_type !== 'REVENUE') {
      submitValues.taxing_choice = 'EXEMPT';
      submitValues.tax_rate_default = 0;
    } else if (submitValues.taxing_choice === 'EXEMPT') {
      submitValues.tax_rate_default = 0;
    }

    // remove empty fields (excluding those we explicitly set for non-REVENUE)
    Object.keys(submitValues).forEach(
      (key) =>
        submitValues[key] === '' &&
        key !== 'taxing_choice' &&
        key !== 'tax_rate_default' &&
        delete submitValues[key]
    );
    Object.keys(submitValues).forEach(
      (key) => submitValues[key] === undefined && delete submitValues[key]
    );

    if (id) {
      return dispatch(SkusActions.fetchUpdateSku(id, submitValues, onSave));
    }
    dispatch(SkusActions.fetchCreateSku(submitValues, 'accounting', exit));
  };

  const hasTaxes = useCallback(
    (accountingType) => {
      return accountingType === 'REVENUE' && taxesEnabled !== false;
    },
    [taxesEnabled]
  );

  const setValuesFn = useCallback(
    (
      setFieldFn,
      accounting_type,
      accounting_type_id,
      taxing_choice,
      tax_rate_default,
      tax_rules_set_id
    ) => {
      if (typeof setFieldFn === 'function') {
        setFieldFn('accounting_type', accounting_type);
        setFieldFn('accounting_type_id', accounting_type_id);
        setFieldFn('taxing_choice', taxing_choice);
        setFieldFn('tax_rate_default', tax_rate_default);
        setFieldFn('tax_rules_set_id', tax_rules_set_id);
      }
    },
    []
  );

  return (
    <Box className={classes.container}>
      <Container className="h-100" style={center ? {} : { padding: 0 }}>
        <Grid
          container
          className={classes.container}
          direction="row"
          justifyContent={center ? 'center' : 'flex-start'}
          alignItems="center"
        >
          <Formik
            initialValues={initialValues}
            validationSchema={formSchema}
            enableReinitialize={true}
            innerRef={formRef}
            onSubmit={onSubmit}
          >
            {({ handleSubmit, values, touched, setFieldValue, errors }) => (
              <form noValidate onSubmit={handleSubmit} style={{ width: '100%', maxWidth: 500 }}>
                <Grid item xs={12}>
                  <Typography variant="h2">
                    <Box fontWeight="fontWeightBold" fontSize={14} style={{ height: '100%' }}>
                      {i18n.t('skus.wizard.accounting_title')}
                    </Box>
                  </Typography>
                  <Grid item xs={12} className={classes.radiosContainer}>
                    <RadioButtonsGroup
                      errors={propErrors || errors}
                      touched={touched}
                      name="accounting_type"
                      value={values.accounting_type}
                      nonBool={true}
                      setFieldValue={setFieldValue}
                    >
                      <div className={classes.radiosLabel}>
                        <Field
                          value={'REVENUE'}
                          component={RadioButton}
                          label={i18n.t('skus.wizard.revenue_label')}
                          data-cy="accounting_type-radio"
                          onChange={() => {
                            setValuesFn(
                              setFieldValue,
                              'REVENUE',
                              '',
                              taxesEnabled === false ? 'EXEMPT' : '',
                              step?.tax_rate_default || undefined,
                              step?.tax_rules_set?.id || undefined
                            );
                          }}
                        />
                        <p className={classes.radiosDescription}>
                          {i18n.t('skus.wizard.revenue_description')}
                        </p>
                      </div>
                      <Divider />
                      <div className={classes.radiosLabel}>
                        <Field
                          value={'EXPENSE'}
                          component={RadioButton}
                          label={i18n.t('skus.wizard.discount_label')}
                          data-cy="discount-radio"
                          onChange={() => {
                            setValuesFn(
                              setFieldValue,
                              'EXPENSE',
                              '',
                              'EXEMPT',
                              0,
                              step?.tax_rules_set?.id || undefined
                            );
                          }}
                        />
                        <p className={classes.radiosDescription}>
                          {i18n.t('skus.wizard.discount_description')}
                        </p>
                      </div>
                      <Divider />
                      {holdEnabled && (
                        <>
                          <div className={classes.radiosLabel}>
                            <Field
                              value={'HOLD'}
                              component={RadioButton}
                              label={i18n.t('skus.wizard.hold_label')}
                              data-cy="hold-radio"
                              onChange={() => {
                                setValuesFn(
                                  setFieldValue,
                                  'HOLD',
                                  '',
                                  'EXEMPT',
                                  0,
                                  step?.tax_rules_set?.id || undefined
                                );
                              }}
                            />
                            <p className={classes.radiosDescription}>
                              {i18n.t('skus.wizard.hold_description')}
                            </p>
                          </div>
                          <Divider />
                        </>
                      )}
                    </RadioButtonsGroup>
                  </Grid>
                  <Grid item xs={12} className={classes.mt}>
                    <Grid container spacing={4}>
                      <Grid item xs={12}>
                        <Typography variant="h2">
                          <Box fontWeight="fontWeightBold" fontSize={14} style={{ height: '100%' }}>
                            {i18n.t('skus.wizard.accounting_type')}
                          </Box>
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Field
                          asyncErrors={errors?.accounting_type_id || propErrors?.accounting_type_id}
                          errors={errors?.accounting_type_id || propErrors?.accounting_type_id}
                          touched={touched}
                          name="accounting_type_id"
                          customParams={{
                            is_revenue: values?.accounting_type === 'REVENUE',
                            type: values?.accounting_type,
                          }}
                          value={values.accounting_type}
                          setFieldValue={setFieldValue}
                          component={AccountingTypesSelect}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  {hasTaxes(values.accounting_type) && (
                    <Grid item xs={12} className={classes.mt}>
                      <Grid container spacing={4}>
                        <Grid item xs={12}>
                          <Typography variant="h2">
                            <Box
                              fontWeight="fontWeightBold"
                              fontSize={14}
                              style={{ height: '100%', marginBottom: 12 }}
                            >
                              {i18n.t('skus.wizard.taxes')}
                            </Box>
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Field
                            margin="none"
                            errors={errors?.taxing_choice || propErrors?.taxing_choice}
                            multiple={false}
                            touched={touched}
                            name="taxing_choice"
                            data-cy="taxing_choice-select"
                            component={TaxTypesSelect}
                            setFieldValue={setFieldValue}
                            value={values.taxing_choice}
                            omitFields={
                              !pricingConfig?.tax_rules_set ? ['PERLOCATION', 'EXTERNAL'] : []
                            }
                            label={i18n.t('skus.wizard.select_tax_type')}
                            customParams={{
                              charge_type:
                                values?.accounting_type === 'REVENUE' ? 'revenue' : 'discount',
                            }}
                            onChange={(val) => {
                              const setDefaultTax =
                                val === 'PERLOCATION' && !values.tax_rate_default;
                              if (setDefaultTax) {
                                setFieldValue('tax_rate_default', 0);
                              }
                            }}
                          />
                          {(values.taxing_choice === 'EXEMPT' ||
                            values.taxing_choice === i18n.t(`skus.tax_types.EXEMPT`)) && (
                            <span className={classes.label}>
                              {i18n.t('skus.tax_is_set_to_zero')}
                            </span>
                          )}
                          {(values.taxing_choice === 'EXTERNAL' ||
                            values.taxing_choice === i18n.t(`skus.tax_types.EXTERNAL`)) && (
                            <span className={classes.label}>{i18n.t('skus.tax_zip_code')}</span>
                          )}
                        </Grid>
                        {(values.taxing_choice === 'DEFAULT' ||
                          values.taxing_choice === i18n.t(`skus.tax_types.DEFAULT`)) && (
                          <Grid item xs={6}>
                            <Field
                              decimals={4}
                              margin="none"
                              type="number"
                              format="price"
                              component={TextField}
                              name="tax_rate_default"
                              asyncErrors={errors?.tax_rate_default || propErrors?.tax_rate_default}
                              label={i18n.t(`skus.default_tax_rate`)}
                            />
                          </Grid>
                        )}
                        {values.taxing_choice === 'PERLOCATION' && (
                          <>
                            <Grid item xs={12} sm={6}>
                              <Field
                                margin="none"
                                type="number"
                                label="Tax Ruleset"
                                name="tax_rules_set_id"
                                data-cy="tax_rules_set_id-select"
                                component={TaxRulesetsSelect}
                                asyncErrors={
                                  errors?.tax_rules_set_id || propErrors?.tax_rules_set_id
                                }
                                customParams={{ rules_set_id: values?.tax_rules_set_id || '' }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Field
                                decimals={4}
                                margin="none"
                                type="number"
                                format="price"
                                component={TextField}
                                name="tax_rate_default"
                                data-cy="tax_rate_default-input"
                                asyncErrors={
                                  errors?.tax_rate_default || propErrors?.tax_rate_default
                                }
                                required={false}
                                onChange={(event) => {
                                  const value = event?.target?.value;
                                  if (!value) {
                                    setFieldValue('tax_rate_default', 0);
                                  } else {
                                    setFieldValue('tax_rate_default', parseFloat(value));
                                  }
                                }}
                                label={
                                  <div className={classes.defaultTaxLabel}>
                                    {i18n.t(`skus.default_no_location`)}*
                                    <Tooltip
                                      title={i18n.t('skus.default_tax_rate_tooltip')}
                                      placement="right"
                                    >
                                      <InfoIcon
                                        fontSize="small"
                                        className={classes.helpIconInline}
                                      />
                                    </Tooltip>
                                  </div>
                                }
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </form>
            )}
          </Formik>
        </Grid>
      </Container>
    </Box>
  );
};

SkuAccounting.propTypes = {
  id: PropTypes.any,
  formRef: PropTypes.any,
  errors: PropTypes.object,
  step: PropTypes.object,
  exit: PropTypes.func,
  onSave: PropTypes.func,
  center: PropTypes.bool,
};

export default SkuAccounting;
